import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';
import { useNavigate } from 'react-router-dom';

import Button from 'components/partials/button/button';
import {
  MONITORING_INTRO_POPUP_OPEN_EVENT,
  SIDE_MENU_POPOVER_ANCHOR_ATTRIBUTE,
  type SideMenuPopoverOpenDetail,
} from 'components/shared/side-menu/side-menu-popover-events';
import { SIDEMENU_Z_INDEX } from 'components/shared/side-menu/side-menu.constants';
import { useSideMenuContext } from 'components/shared/side-menu/side-menu-layout';
import useSideMenuPopoverPosition from 'components/shared/side-menu/hooks/use-side-menu-popover-position';
import { useIsAnyFeatureHintActive } from 'components/shared/feature-hints';

import { useCurrentUser } from 'hooks/api/use-current-user';
import { useWorkspaceRoute } from 'hooks/use-workspace-route';
import { useClickOutside } from 'hooks/use-click-outside';

import {
  trackMonitoringWalkthroughDismissed,
  trackMonitoringWalkthroughViewed,
} from 'containers/user/critical-elements-monitoring/analytics/monitoring-walkthrough-analytics';
import type { MonitoringWalkthroughDismissMethod } from 'containers/user/critical-elements-monitoring/analytics/monitoring-walkthrough-analytics.constants';
import { recordMonitoringTourSeen } from './monitoring-walkthrough-storage';
import { requestMonitoringTourStart } from './monitoring-tour-start-request';

import Icons from 'assets/icons';

const MONITORING_INTRO_ANCHOR_SELECTOR = `[${SIDE_MENU_POPOVER_ANCHOR_ATTRIBUTE}="monitoring-intro"]`;
const MONITORING_INTRO_TITLE = 'Monitoring';
const MONITORING_INTRO_BODY =
  "This is Monitoring: a full-funnel view of your enrollment performance from inquiry through deposit. Track your total funnel here, then drill into your Critical Elements below to see where you're gaining ground and where you need to focus.";
const MONITORING_PORTAL_ROUTE = '/monitoring';

const MonitoringIntroPopover = (): React.JSX.Element | null => {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const { isCollapsed } = useSideMenuContext();
  const { isAdminWorkspace, selectedClientId, resolveRoute } =
    useWorkspaceRoute();
  const isAnalyticsEnabled = !isAdminWorkspace;
  const isHintTourActive = useIsAnyFeatureHintActive();

  const [visible, setVisible] = useState(false);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [fallbackAnchorElement, setFallbackAnchorElement] =
    useState<HTMLElement | null>(null);
  const [anchorGap, setAnchorGap] = useState<number | undefined>();

  const popupRef = useRef<HTMLDivElement>(null);
  const visibleRef = useRef(visible);
  const suppressReopenRef = useRef(false);
  const isHintTourActiveRef = useRef(isHintTourActive);

  const anchor = useSideMenuPopoverPosition(
    MONITORING_INTRO_ANCHOR_SELECTOR,
    isCollapsed,
    {
      enabled: visible,
      anchorElement,
      fallbackAnchorElement,
      anchorGap,
    }
  );

  visibleRef.current = visible;
  isHintTourActiveRef.current = isHintTourActive;

  useEffect(() => {
    const handleOpen = (event: Event): void => {
      if (suppressReopenRef.current) {
        suppressReopenRef.current = false;

        return;
      }

      if (isHintTourActiveRef.current) return;

      const detail =
        event instanceof CustomEvent
          ? ((event.detail ?? {}) as SideMenuPopoverOpenDetail)
          : {};

      if (detail.repositionOnly && !visibleRef.current) return;

      const wasVisible = visibleRef.current;

      setAnchorElement(detail.anchorElement ?? null);
      setFallbackAnchorElement(detail.fallbackAnchorElement ?? null);
      setAnchorGap(detail.anchorGap);
      visibleRef.current = true;
      setVisible(true);

      if (!wasVisible) {
        detail.onAutoOpened?.();

        if (isAnalyticsEnabled) {
          trackMonitoringWalkthroughViewed({ section: 'introPopup' });
        }
      }
    };

    window.addEventListener(MONITORING_INTRO_POPUP_OPEN_EVENT, handleOpen);

    return () => {
      window.removeEventListener(MONITORING_INTRO_POPUP_OPEN_EVENT, handleOpen);
    };
  }, [isAnalyticsEnabled]);

  const closePopup = useCallback(() => {
    suppressReopenRef.current = true;

    setAnchorElement(null);
    setFallbackAnchorElement(null);
    setAnchorGap(undefined);
    visibleRef.current = false;
    setVisible(false);

    window.setTimeout(() => {
      suppressReopenRef.current = false;
    }, 0);
  }, []);

  useEffect(() => {
    if (isHintTourActive && visibleRef.current) closePopup();
  }, [isHintTourActive, closePopup]);

  const handleDismiss = useCallback(
    (dismissMethod: MonitoringWalkthroughDismissMethod) => {
      if (isAnalyticsEnabled) {
        trackMonitoringWalkthroughDismissed({
          section: 'introPopup',
          dismissMethod,
        });
      }
      closePopup();
    },
    [closePopup, isAnalyticsEnabled]
  );

  const handleClickOutside = useCallback(() => {
    handleDismiss('clickOutside');
  }, [handleDismiss]);

  const monitoringRoute = resolveRoute(
    `/admin/clients/${selectedClientId}/monitoring`,
    MONITORING_PORTAL_ROUTE
  );

  const handleExplore = useCallback(() => {
    recordMonitoringTourSeen(user?.data?.oktaUserId);
    requestMonitoringTourStart();
    closePopup();
    navigate(monitoringRoute);
  }, [closePopup, navigate, monitoringRoute, user?.data?.oktaUserId]);

  const showPopup = visible && anchor;

  useClickOutside(popupRef, handleClickOutside, visible);

  useEffect(() => {
    if (!visible) return undefined;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        handleDismiss('escape');
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleDismiss, visible]);

  if (!showPopup) return null;

  return (
    <div
      ref={popupRef}
      role="dialog"
      aria-label={MONITORING_INTRO_TITLE}
      aria-live="polite"
      tabIndex={-1}
      className="fixed flex items-start animate-fade-in outline-none"
      style={{
        top: anchor.top,
        left: anchor.left,
        zIndex: SIDEMENU_Z_INDEX.popover,
      }}
    >
      <div
        aria-hidden="true"
        className="relative z-10 flex items-center h-8 shrink-0 -mr-1.5"
      >
        <div className="size-3 rotate-45 border-b border-l border-navy-200 bg-navy-50" />
      </div>

      <div className="flex w-96 flex-col gap-4 rounded-xl border border-navy-200 bg-navy-50 p-5">
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-bold leading-6 text-navy-800">
            {MONITORING_INTRO_TITLE}
          </h3>
          <p className="text-sm text-gray-700">{MONITORING_INTRO_BODY}</p>
        </div>

        <div className="flex items-center justify-between gap-4">
          <Button
            type="button"
            variant="reserve-text-link"
            size="sm"
            className="p-0"
            onClick={() => handleDismiss('maybeLater')}
            data-testid="monitoring-intro-maybe-later"
          >
            Maybe later
          </Button>
          <Button
            type="button"
            variant="reserve-filled"
            size="xs"
            iconAfter={<Icons.ArrowUpRight />}
            onClick={handleExplore}
            data-testid="monitoring-intro-explore"
          >
            Explore
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MonitoringIntroPopover;
